/** 将会话图片绘制为独立 GL 变形预览，通过 Canvas 舞台定位组件跟随平移和缩放 */
import glfx from 'glfx';
import { flatten } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { BaseOperateView, type IFramework } from '@/framework';
import { loadImageAsync } from '@/utils/file-kit';
import { calculateBoundingBox } from '@/utils/math-kit';
import type { ILayerProps } from '@/version-types';
import { useDeformationImgState } from '../states/deformation-img';
import { EDeformationEvent, RectangleAnchorPointName, type CornerPoint } from '../types';
import DeformationImgOperate from './deformation-img-operate';
import RenderStage, { StageWidthScale } from './render-stage';

const getInitPoints = (layer: ILayerProps | null): CornerPoint[] => {
    if (!layer) return [];
    const { width, height } = layer;
    const left = (width * StageWidthScale - width) / 2;
    const top = (height * StageWidthScale - height) / 2;
    return [
        { left, top, type: RectangleAnchorPointName.LEFT_TOP },
        { left: left + width, top, type: RectangleAnchorPointName.RIGHT_TOP },
        { left: left + width, top: top + height, type: RectangleAnchorPointName.RIGHT_BOTTOM },
        { left, top: top + height, type: RectangleAnchorPointName.LEFT_BOTTOM },
    ];
};

const DeformationImgWrap: React.FC<{ scale: number; framework: IFramework }> = ({ scale, framework }) => {
    const activeLayer = useDeformationImgState()((state) => state.currentLayer);
    const layerImage = useDeformationImgState()((state) => state.layerImage);
    const isSaving = useDeformationImgState()((state) => state.isSaving);
    const [latestPoints, setLatestPoints] = useState<CornerPoint[]>(() => getInitPoints(activeLayer));
    const [startPoints] = useState<CornerPoint[]>(() => getInitPoints(activeLayer));
    const canvasHostRef = useRef<HTMLDivElement>(null);
    const glfxCanvas = useRef<typeof glfx>(null);
    const texture = useRef<typeof glfx.texture>(null);
    const confirming = useRef(false);

    useEffect(() => {
        if (!activeLayer || !layerImage || !canvasHostRef.current) return;
        try {
            const imgCanvas = document.createElement('canvas');
            imgCanvas.width = activeLayer.width * StageWidthScale;
            imgCanvas.height = activeLayer.height * StageWidthScale;
            const ctx = imgCanvas.getContext('2d');
            if (!ctx) throw new Error('Deformation canvas context unavailable');
            ctx.drawImage(
                layerImage,
                (imgCanvas.width - activeLayer.width) / 2,
                (imgCanvas.height - activeLayer.height) / 2,
                activeLayer.width,
                activeLayer.height,
            );
            const canvas = glfx.canvas();
            canvas.className = 'deformation-img-canvas';
            glfxCanvas.current = canvas;
            texture.current = canvas.texture(imgCanvas);
            canvas.draw(texture.current).update();
            canvasHostRef.current.replaceChildren(canvas);
        } catch (error) {
            console.error('Initialize deformation image failed', error);
            framework.service.events.emit(EDeformationEvent.ERROR, error, true);
        }
        return () => {
            texture.current?.destroy?.();
            glfxCanvas.current?.destroy?.();
            glfxCanvas.current = null;
            texture.current = null;
        };
    }, [activeLayer, layerImage, framework]);

    useEffect(() => {
        if (!startPoints.length || !latestPoints.length) return;
        const formatPoints = (points: CornerPoint[]) => flatten(points.map((point) => [point.left, point.top]));
        glfxCanvas.current
            ?.draw(texture.current!)
            .perspective(formatPoints(startPoints), formatPoints(latestPoints))
            .update();
    }, [latestPoints, startPoints]);

    useEffect(() => {
        let disposed = false;
        const handle = async () => {
            const fxCanvas = glfxCanvas.current;
            if (!fxCanvas || !activeLayer || confirming.current || isSaving) return;
            confirming.current = true;
            try {
                fxCanvas.update();
                const image = await loadImageAsync(fxCanvas.toDataURL());
                if (disposed) return;
                const box = calculateBoundingBox(latestPoints.map((point) => [point.left, point.top]));
                const minX = Math.floor(box.minX);
                const minY = Math.floor(box.minY);
                const canvas = document.createElement('canvas');
                canvas.width = Math.ceil(box.maxX) - minX;
                canvas.height = Math.ceil(box.maxY) - minY;
                const ctx = canvas.getContext('2d');
                if (!ctx || !canvas.width || !canvas.height) throw new Error('Empty deformation result');
                ctx.drawImage(image, minX, minY, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
                framework.service.events.emit(EDeformationEvent.CONFIRM, canvas, {
                    left: minX - (activeLayer.width * StageWidthScale - activeLayer.width) / 2,
                    top: minY - (activeLayer.height * StageWidthScale - activeLayer.height) / 2,
                });
            } catch (error) {
                console.error('Export deformation image failed', error);
                if (!disposed) framework.service.events.emit(EDeformationEvent.ERROR, error, false);
            } finally {
                confirming.current = false;
            }
        };
        const id = framework.service.events.on(EDeformationEvent.INNER_CONFIRM, handle);
        return () => {
            disposed = true;
            framework.service.events.off(id);
        };
    }, [activeLayer, framework, isSaving, latestPoints]);

    if (!activeLayer) return null;
    return (
        <BaseOperateView layer={activeLayer} style={{ opacity: activeLayer.opacity }}>
            <RenderStage scale={scale} activeLayer={activeLayer} canvasHostRef={canvasHostRef} />
            <DeformationImgOperate
                scale={scale}
                framework={framework}
                layer={activeLayer}
                latestPoints={latestPoints}
                setLatestPoints={setLatestPoints}
                maxWidth={activeLayer.width * StageWidthScale}
                maxHeight={activeLayer.height * StageWidthScale}
            />
        </BaseOperateView>
    );
};

export default DeformationImgWrap;
